import {
  BarChart3,
  FileJson,
  Route,
  MousePointerClick,
  UserPlus,
  Upload,
  ListChecks,
  Calendar,
  Settings,
  LayoutDashboard,
  Users,
  ShieldCheck,
  Wrench,
  Banknote,
  KeyRound,
  Receipt,
  Layers,
  TrendingDown,
  LucideIcon,
  UploadCloud,
  Activity,
  Database,
} from "lucide-react";

export interface MenuItem {
  id: string;
  label: string;
  icon: LucideIcon;
  userTypes?: string[];
  isSuperuserOnly?: boolean;
  hideFromMenu?: boolean;
}

export interface MenuCategory {
  title: string;
  userTypes?: string[];
  isSuperuserOnly?: boolean;
  items: MenuItem[];
}

export const SIDEBAR_CONFIG: MenuCategory[] = [
  {
    title: "Analytics",
    items: [
      {
        id: "analytics",
        label: "C-Suite MIS",
        icon: BarChart3,
      },
      {
        id: "journey-analysis",
        label: "Journey Analysis",
        icon: Route,
      },
      {
        id: "funnel-dashboard",
        label: "Funnel Dashboard",
        icon: TrendingDown,
      },
      // @origin: v3 | @since: native | @used-by: LND:CAPRION | @purpose:
      // "View Journey Status" — one row per user with application_id, derived
      // final_status, every check_status response, current journey page and
      // source_slug, plus filters/search and a field-selectable CSV export.
      // See CaprionJourneyStatus.tsx. NOT isSuperuserOnly here because this
      // flag only hides the sidebar link; the real boundary is the backend's
      // is_superuser check on /api/admin/caprion/journey_status/, and the
      // screen renders an explicit "superuser required" panel on a 403.
      {
        id: "caprion-journey-status",
        label: "BRE Analysis (Caprion)",
        icon: FileJson,
      },
      // @origin: v2 | @since: 3d9ebf6d | @used-by: ALL | @purpose: 2f port --
      // Stage & Page Analytics tab (Stage Analysis + Page Time Analytics
      // sub-tabs). See JourneyStageAnalytics.tsx.
      {
        id: "stage-analytics",
        label: "Stage & Page Analytics",
        icon: Layers,
      },
      {
        id: "link-click",
        label: "Link Click",
        icon: MousePointerClick,
      }
    ]
  },
  {
    title: "CRM",
    items: [
      { id: "crm-leads", label: "Add Lead", icon: UserPlus },
      { id: "leads-table", label: "Lead Table", icon: UserPlus },
      { id: "todays-tasks", label: "Todays Tasks", icon: ListChecks },
      { id: "tomorrows-tasks", label: "Tomorrows Tasks", icon: Calendar },
      { id: "crm-settings", label: "CRM Settings", icon: Settings },
    ]
  },
  {
    title: "CRM - Manager",
    items: [
      { id: "bulk-upload", label: "Bulk Upload", icon: Upload },
      {
        id: "crm-config",
        label: "CRM Config",
        icon: ListChecks,
      }
    ]
  },
  {
    /**
     * Parallel LMS Staff Management System (plan §8).
     *
     * `lms-staff-ops` is intentionally NOT superuser-only: the hub itself is
     * safe for any staff member to open because it reads
     * /api/lms/admin/my_permissions/ and renders only the tabs that user has
     * `can_view` on (showing a "no tabs available" panel when that is none).
     * Which staff can see the menu entry at all is therefore controlled the
     * normal way, through Role.permissions containing the "lms-staff-ops" id.
     *
     * `lms-staff-permissions` IS superuser-only, per the explicit requirement
     * that only superusers may allot permissions and staff must not be able to
     * change anything about them. The backend enforces the same rule with
     * IsSuperUser on every endpoint behind that screen.
     */
    title: "LMS Staff Ops",
    items: [
      { id: "lms-staff-ops", label: "LMS Operations", icon: Banknote },
      {
        id: "lms-staff-permissions",
        label: "LMS Permissions",
        icon: KeyRound,
        isSuperuserOnly: true
      }
    ]
  },
  {
    /**
     * Disbursed Loan Details.
     *
     * NOT superuser-only, so it can be granted to internal roles like any
     * other menu item. Note there are TWO independent permissions in play:
     * this `lms-disbursed-loans` id only decides whether the sidebar link is
     * rendered, while the API behind the screen is gated by the separate
     * `disbursed_loans` LmsStaffPermission tab (enforced by
     * HasLmsTabPermission in backend/lms/views_staff_ops.py). A role granted
     * the menu entry but not the tab permission sees the screen explain the
     * 403 rather than fail silently.
     */
    title: "LMS",
    items: [
      { id: "lms-disbursed-loans", label: "Disbursed Loans", icon: Receipt },
      { id: "lms-upload-daily-collection", label: "Upload Daily Collection", icon: UploadCloud },
      { id: "lms-intraday-bounce", label: "Intra-Day Bounce", icon: Activity },
    ]
  },
  {
    title: "SQL Script Executor",
    items: [
      {
        id: "thuriyam-dashboard",
        label: "SQL Scripts",
        icon: Database,
      },
    ],
  },
  {
    title: "IAdmin Tools",
    items: [
      { id: "tools", label: "IAdmin Tools", icon: LayoutDashboard },
      { id: "iadmin-fixer", label: "Fixer", icon: Wrench },
      /**
       * Expenses — Digitap vendor-API spend/observability panel.
       * @origin: v2 | @since: 0570ba2d | @used-by: ALL
       *
       * `isSuperuserOnly` mirrors v2's own menu entry AND the backend guard:
       * GET /api/admin/digitap_logs/ refuses anything that is not
       * `is_superuser` (backend/los_app/admin_views.py). This flag is a UI
       * affordance only — it hides the link, it is NOT the security boundary.
       * `is_staff` is deliberately insufficient there, because los_app's
       * `createadminuser` command provisions real is_staff/not-is_superuser
       * accounts and those users must not see borrower PII. ExpensesPanel
       * still renders an explicit "superuser only" state if the 403 is ever
       * reached (e.g. a superuser flag revoked mid-session).
       */
      { id: "iadmin-expenses", label: "Expenses", icon: Receipt, isSuperuserOnly: true }
    ]
  }
];

export const FOOTER_CONFIG: MenuCategory[] = [
  {
    title: "Settings",
    userTypes: ["INTERNAL", "BAP", "LE", "SALES AGENT", "SALES LEAD"],
    items: [
      { id: "general-settings", label: "General Settings", icon: Settings }
    ]
  },
  {
    title: "System",
    items: [
      { 
        id: "Admin Login", 
        label: "Admin Login", 
        icon: ShieldCheck, 
        hideFromMenu: true 
      }
    ]
  }
];
